/**
 * Helper específico para detectar cambios en conceptos
 * Utiliza los helpers genéricos de changeDetectionHelper
 */

import { DataTableColumn, DataType, Section, SelectOption } from '@globaleris/ui.library'
import { TFunction } from 'i18next'
import {
  ConceptLayoutConfigurationModel,
  EditConceptModel
} from '../../api/Argentia/endpoints/concepts/model/concepts'
import { EnumPeriodo } from '../../api/Argentia/enums/periodo'
import {
  EnumRegionImpuestos,
  EnumTipoCaudal,
  EnumTipoPeaje,
  EnumTipoProductoCapacidad,
  EnumTipoUso
} from '../../api/oData/enums'
import { enumToSelectOptionArray } from '../../helpers'
import {
  detectArrayChanges,
  detectPrimitiveChanges,
  hasObjectChanged
} from '../../helpers/changeDetectionHelper'

/**
 * Modelo para el request de actualización de concepto (Patch)
 * Solo incluye lo que cambió, evitando operaciones innecesarias en el backend
 */
export interface ConceptPatchRequest {
  id: number

  // Propiedades primitivas (solo se incluyen si cambiaron)
  code?: string
  description?: string
  conceptType?: number
  calculationType?: number
  currency?: number
  commodity?: number
  companyId?: number
  countryId?: number
  IVAType?: number
  pxq?: boolean
  lgBypass?: boolean
  boolDescription?: boolean

  // Operaciones explícitas en translations
  translationsToCreate?: Array<{
    language: number
    description: string
  }>
  translationsToUpdate?: Array<{
    id: number
    language: number
    description: string
  }>
  translationIdsToDelete?: number[]

  // Operaciones explícitas en quantities
  quantitiesToCreate?: Array<{
    data?: number | null
    measureUnit?: number | null
    documentType?: number | null
  }>
  quantitiesToUpdate?: Array<{
    id: number
    data?: number | null
    measureUnit?: number | null
    documentType?: number | null
  }>
  quantityIdsToDelete?: number[]

  // Operaciones explícitas en prices (si aplica en el futuro)
  pricesToCreate?: Array<any>
  pricesToUpdate?: Array<any>
  priceIdsToDelete?: number[]
}

/**
 * Resultado del análisis completo de cambios en un concepto
 */
export interface ConceptChangeAnalysis {
  hasChanges: boolean
  primitiveChanges: Record<string, any>
  translationsChanged: boolean
  quantitiesChanged: boolean
  pricesChanged: boolean
}

/**
 * Propiedades primitivas del concepto que se deben comparar
 */
const CONCEPT_PRIMITIVE_KEYS = [
  'code',
  'description',
  'conceptType',
  'calculationType',
  'currency',
  'commodity',
  'companyId',
  'countryId',
  'IVAType',
  'pxq',
  'lgBypass',
  'boolDescription'
]

/**
 * Analiza todos los cambios en un concepto
 * @param original - Concepto original (estado inicial)
 * @param current - Concepto actual (con cambios del usuario)
 * @returns Análisis detallado de los cambios
 */
export function analyzeConceptChanges(
  original: EditConceptModel,
  current: EditConceptModel
): ConceptChangeAnalysis {
  // Detectar cambios en propiedades primitivas
  const primitiveResult = detectPrimitiveChanges(original, current, CONCEPT_PRIMITIVE_KEYS)

  // Detectar cambios en translations
  const translationChanges = detectArrayChanges(
    original.translations || [],
    current.translations || []
  )

  // Detectar cambios en quantities
  const quantityChanges = detectArrayChanges(
    original.conceptQuantities || [],
    current.conceptQuantities || []
  )

  // TODO: Detectar cambios en prices cuando se implemente
  const pricesChanged = false

  return {
    hasChanges:
      primitiveResult.hasChanges ||
      translationChanges.hasChanges ||
      quantityChanges.hasChanges ||
      pricesChanged,
    primitiveChanges: primitiveResult.changed,
    translationsChanged: translationChanges.hasChanges,
    quantitiesChanged: quantityChanges.hasChanges,
    pricesChanged
  }
}

/**
 * Construye el request de actualización (Patch) con solo lo que cambió
 * @param original - Concepto original (estado inicial)
 * @param current - Concepto actual (con cambios del usuario)
 * @returns Request optimizado para enviar al backend
 */
export function buildConceptPatchRequest(
  original: EditConceptModel,
  current: EditConceptModel
): ConceptPatchRequest {
  const request: ConceptPatchRequest = { id: current.id }

  // 1. Agregar propiedades primitivas que cambiaron
  const primitiveChanges = detectPrimitiveChanges(original, current, CONCEPT_PRIMITIVE_KEYS)
  if (primitiveChanges.hasChanges) {
    Object.assign(request, primitiveChanges.changed)
  }

  // 2. Agregar operaciones de translations
  const translationChanges = detectArrayChanges(
    original.translations || [],
    current.translations || []
  )

  if (translationChanges.toCreate.length > 0) {
    request.translationsToCreate = translationChanges.toCreate.map(t => ({
      language: t.language,
      description: t.description
    }))
  }

  if (translationChanges.toUpdate.length > 0) {
    request.translationsToUpdate = translationChanges.toUpdate.map(t => ({
      id: t.id!,
      language: t.language,
      description: t.description
    }))
  }

  if (translationChanges.toDelete.length > 0) {
    request.translationIdsToDelete = translationChanges.toDelete
  }

  // 3. Agregar operaciones de quantities
  const quantityChanges = detectArrayChanges(
    original.conceptQuantities || [],
    current.conceptQuantities || []
  )

  if (quantityChanges.toCreate.length > 0) {
    request.quantitiesToCreate = quantityChanges.toCreate.map(q => ({
      data: q.data,
      measureUnit: q.measureUnit,
      documentType: q.documentType
    }))
  }

  if (quantityChanges.toUpdate.length > 0) {
    request.quantitiesToUpdate = quantityChanges.toUpdate.map(q => ({
      id: q.id!,
      data: q.data,
      measureUnit: q.measureUnit,
      documentType: q.documentType
    }))
  }

  // TODO: 4. Agregar operaciones de prices cuando se implemente
  // Por ahora se está disparando el cambio al agregar / eliminar un conceptPrice por lo que no es necesario que forme parte del request.
  return request
}

/**
 * Valida si un concepto tiene cambios (útil antes de guardar)
 * @param original - Concepto original
 * @param current - Concepto actual
 * @returns true si hay cambios, false si no
 */
export function hasConceptChanged(original: EditConceptModel, current: EditConceptModel): boolean {
  return hasObjectChanged(original, current)
}

/**
 * Construye un patch request solo con propiedades primitivas desde formData
 * Útil para formularios que solo editan la cabecera (sin relaciones)
 * @param conceptId - ID del concepto
 * @param formData - Datos del formulario (solo propiedades modificadas)
 * @returns Patch request optimizado solo con primitivas
 */
export function buildPrimitivePatchRequest(
  conceptId: number,
  formData: Record<string, any>
): ConceptPatchRequest {
  const request: ConceptPatchRequest = { id: conceptId }

  // Solo incluir propiedades primitivas que están definidas en formData
  CONCEPT_PRIMITIVE_KEYS.forEach(key => {
    if (formData[key] !== undefined) {
      request[key] = formData[key]
    }
  })

  return request
}

/**
 * Lógica de renderizado condicional según tipoCalculoConcepto (no incluido en tabla EVG_T_R_TIPO_CALCULO_CONCEPTO_LAYOUT)
 * @param calculationType - Tipo de cálculo del concepto
 * @returns true si se deben renderizar conceptos relacionados, false si no
 */
export type ConceptPriceDiscriminantField =
  | 'toll'
  | 'usage'
  | 'flow'
  | 'duration'
  | 'taxRegion'
  | 'period'

/**
 * Construye los inputs de Select para los discriminantes de precio (peaje, uso, caudal,
 * duración, región impuestos, período) según el layout de cálculo del concepto.
 * Cada input solo se muestra/requiere si el flag correspondiente de `configuration` está activo.
 * Reutilizado por ConceptPriceEditDialog y los formularios add/edit de conceptModified.
 */
export function buildDiscriminantFormInputs(
  configuration: ConceptLayoutConfigurationModel,
  t: TFunction,
  onDiscriminantChange: (
    field: ConceptPriceDiscriminantField,
    value: number,
    formState: any
  ) => void,
  // Permite, p.ej., filtrar opciones para evitar combinaciones de discriminantes duplicadas
  // (uso en ConceptPriceEditDialog). Si no se pasa, se usan las opciones del enum tal cual.
  getSelectOptions?: (
    field: ConceptPriceDiscriminantField,
    defaultOptions: SelectOption[]
  ) => SelectOption[]
): Section['inputs'] {
  const resolveOptions = (field: ConceptPriceDiscriminantField, defaultOptions: SelectOption[]) =>
    getSelectOptions ? getSelectOptions(field, defaultOptions) : defaultOptions

  return [
    {
      name: 'toll',
      label: t('conceptsEdit:components.conceptPriceCard.dataTable.columns.toll'),
      type: DataType.SELECT,
      selectOptions: resolveOptions(
        'toll',
        enumToSelectOptionArray(EnumTipoPeaje, 'enums:EnumTipoPeaje')
      ),
      display: configuration.lgToll,
      required: configuration.lgToll,
      onChange: (value, formState) => onDiscriminantChange('toll', value as number, formState)
    },
    {
      name: 'usage',
      label: t('conceptsEdit:components.conceptPriceCard.dataTable.columns.usage'),
      type: DataType.SELECT,
      selectOptions: resolveOptions(
        'usage',
        enumToSelectOptionArray(EnumTipoUso, 'enums:EnumTipoUso')
      ),
      display: configuration.lgUsage,
      required: configuration.lgUsage,
      onChange: (value, formState) => onDiscriminantChange('usage', value as number, formState)
    },
    {
      name: 'flow',
      label: t('conceptsEdit:components.conceptPriceCard.dataTable.columns.flow'),
      type: DataType.SELECT,
      selectOptions: resolveOptions(
        'flow',
        enumToSelectOptionArray(EnumTipoCaudal, 'enums:EnumTipoCaudal')
      ),
      display: configuration.lgUnitPriceFlow,
      required: configuration.lgUnitPriceFlow,
      onChange: (value, formState) => onDiscriminantChange('flow', value as number, formState)
    },
    {
      name: 'duration',
      label: t('conceptsEdit:components.conceptPriceCard.dataTable.columns.duration'),
      type: DataType.SELECT,
      selectOptions: resolveOptions(
        'duration',
        enumToSelectOptionArray(EnumTipoProductoCapacidad, 'enums:EnumTipoProductoCapacidad')
      ),
      display: configuration.lgDuration,
      required: configuration.lgDuration,
      onChange: (value, formState) => onDiscriminantChange('duration', value as number, formState)
    },
    {
      name: 'taxRegion',
      label: t('conceptsEdit:components.conceptPriceCard.dataTable.columns.region'),
      type: DataType.SELECT,
      selectOptions: resolveOptions(
        'taxRegion',
        enumToSelectOptionArray(EnumRegionImpuestos, 'enums:EnumRegionImpuestos')
      ),
      display: configuration.lgRegion,
      required: configuration.lgRegion,
      onChange: (value, formState) => onDiscriminantChange('taxRegion', value as number, formState)
    },
    {
      name: 'period',
      label: t('conceptsEdit:components.conceptPriceCard.dataTable.columns.period'),
      type: DataType.SELECT,
      selectOptions: resolveOptions(
        'period',
        enumToSelectOptionArray(EnumPeriodo, 'enums:EnumPeriodo').filter(opt => opt.value !== 7) // Excluye Periodo0
      ),
      display: configuration.lgPeriod,
      required: configuration.lgPeriod,
      onChange: (value, formState) => onDiscriminantChange('period', value as number, formState)
    }
  ]
}

/**
 * Construye columnas de tabla (solo lectura) para los discriminantes de precio, incluyendo
 * únicamente aquellos que tengan valor en al menos una fila de `rows` (sin pedir la configuración
 * de layout por cada calculationType presente en la tabla).
 */
export function buildDiscriminantTableColumns<
  T extends Partial<Record<ConceptPriceDiscriminantField, any>>
>(rows: T[] | undefined, t: TFunction): DataTableColumn[] {
  const hasValue = (field: ConceptPriceDiscriminantField) =>
    (rows ?? []).some(row => row[field] != null)

  const columns: Array<{ field: ConceptPriceDiscriminantField; column: DataTableColumn }> = [
    {
      field: 'toll',
      column: {
        name: 'toll',
        label: t('conceptsEdit:components.conceptPriceCard.dataTable.columns.toll'),
        type: DataType.SELECT,
        selectOptions: enumToSelectOptionArray(EnumTipoPeaje, 'enums:EnumTipoPeaje')
      }
    },
    {
      field: 'usage',
      column: {
        name: 'usage',
        label: t('conceptsEdit:components.conceptPriceCard.dataTable.columns.usage'),
        type: DataType.SELECT,
        selectOptions: enumToSelectOptionArray(EnumTipoUso, 'enums:EnumTipoUso')
      }
    },
    {
      field: 'flow',
      column: {
        name: 'flow',
        label: t('conceptsEdit:components.conceptPriceCard.dataTable.columns.flow'),
        type: DataType.SELECT,
        selectOptions: enumToSelectOptionArray(EnumTipoCaudal, 'enums:EnumTipoCaudal')
      }
    },
    {
      field: 'duration',
      column: {
        name: 'duration',
        label: t('conceptsEdit:components.conceptPriceCard.dataTable.columns.duration'),
        type: DataType.SELECT,
        selectOptions: enumToSelectOptionArray(
          EnumTipoProductoCapacidad,
          'enums:EnumTipoProductoCapacidad'
        )
      }
    },
    {
      field: 'taxRegion',
      column: {
        name: 'taxRegion',
        label: t('conceptsEdit:components.conceptPriceCard.dataTable.columns.region'),
        type: DataType.SELECT,
        selectOptions: enumToSelectOptionArray(EnumRegionImpuestos, 'enums:EnumRegionImpuestos')
      }
    },
    {
      field: 'period',
      column: {
        name: 'period',
        label: t('conceptsEdit:components.conceptPriceCard.dataTable.columns.period'),
        type: DataType.SELECT,
        selectOptions: enumToSelectOptionArray(EnumPeriodo, 'enums:EnumPeriodo').filter(
          opt => opt.value !== 7
        )
      }
    }
  ]

  return columns.filter(({ field }) => hasValue(field)).map(({ column }) => column)
}

/**
 * Indica si el layout del concepto tiene al menos un discriminante de precio activo
 * (peaje, uso, caudal, duración, región impuestos o período). Útil para no renderizar
 * la sección/bloque de "Valores" cuando quedaría vacío (todos los flags lgXxx en false).
 */
export function hasAnyDiscriminantField(configuration: ConceptLayoutConfigurationModel): boolean {
  return (
    !!configuration.lgToll ||
    !!configuration.lgUsage ||
    !!configuration.lgUnitPriceFlow ||
    !!configuration.lgDuration ||
    !!configuration.lgRegion ||
    !!configuration.lgPeriod
  )
}

export function isConceptoAgregador(calculationType?: number): boolean {
  return (
    calculationType === 3 ||
    calculationType === 15 ||
    calculationType === 18 ||
    calculationType === 23 ||
    calculationType === 28 ||
    calculationType === 31 ||
    calculationType === 32 ||
    calculationType === 33 ||
    calculationType === 38 ||
    calculationType === 41 ||
    calculationType === 42 ||
    calculationType === 43 ||
    calculationType === 45 ||
    calculationType === 46
  )
}
